import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Select, Button, Icon, Row, Col, Alert, Spin } from 'antd'
import _ from 'lodash'
import { useDeepEffect } from '../../../../utils/hooks'
import {
  POINT_CHANNEL_SELECTABLE,
  GAIN_TYPE_WITH_HERO,
  GAIN_TYPE_WITH_ACTIVITY,
  GAIN_TYPE_WITH_CHANNEL
} from '../../../../utils/constants'
import { fetchBrandList } from '../../../../modules/brand'
import { getAllHeroes } from '../../../../modules/hero'
import Http from '../../../../api/Http'
import * as EndPoints from '../../../../api/EndPoints'

const HERO_SEARCH_OPTIONS = { page: 0, size: 20, keyword: '' }

const emptyRule = () => ({
  gainTypeId: undefined,
  channel: null,
  heroId: null,
  activityId: null,
  brandId: null
})

/**
 * ตัวแก้เงื่อนไขการแลกของรางวัล (spec 013)
 *
 * แต่ละแถวคือหนึ่งเงื่อนไข ช่องที่เว้นว่าง = "ทั้งหมด"
 * หลายแถว = หรือ (OR) — ต้องสื่อให้ชัด ไม่ให้เข้าใจผิดว่าเป็น "และ"
 */
const ScopeRuleBuilder = (props) => {
  const { value, onChange, gainTypeList } = props
  const dispatch = useDispatch()
  const [heroKeyword, setHeroKeyword] = useState('')
  // เก็บกิจกรรมแยกตามแบรนด์ ไม่ใช้ state ก้อนเดียวใน redux
  // เพราะเงื่อนไขหลายแถวเลือกคนละแบรนด์ได้ ถ้าใช้ก้อนเดียวแถวก่อนหน้า
  // จะแสดงกิจกรรมของแบรนด์ที่โหลดทีหลัง แล้วแอดมินเลือกผิดแบบไม่รู้ตัว
  const [activitiesByBrand, setActivitiesByBrand] = useState({})

  const { brandList, heroList, heroLoading } = useSelector(
    (state) => ({
      brandList: state.brand.brandList,
      heroList: state.hero.heroList,
      heroLoading: state.hero.isLoading
    }),
    []
  )

  async function loadActivities(brandId) {
    if (!brandId || activitiesByBrand[brandId]) return
    try {
      // ต้องเป็น POST พร้อม body ตามที่ modules/brandActivity.js ใช้อยู่
      // ของเดิมผมเรียก GET พร้อม query param ซึ่งไม่มี endpoint นั้นจริง
      const response = await Http.post(EndPoints.BRAND_ACTIVITY + '/activity-list', {
        brandId: brandId,
        enable: false
      })
      const list = (response.data && response.data.brandActivityList) || []
      setActivitiesByBrand((prev) => ({ ...prev, [brandId]: list }))
    } catch (err) {
      setActivitiesByBrand((prev) => ({ ...prev, [brandId]: [] }))
    }
  }

  useDeepEffect(() => {
    dispatch(fetchBrandList())
    // เงื่อนไขที่โหลดมาแก้ไข อาจอ้างหลายแบรนด์ ต้องโหลดกิจกรรมของทุกแบรนด์ที่ถูกอ้าง
    value
      .map((rule) => rule.brandId)
      .filter((brandId) => brandId)
      .forEach(loadActivities)
  }, [])

  const heroOptions = (heroList && heroList.content) || []

  function updateRule(index, patch) {
    const next = value.map((rule, i) => (i === index ? { ...rule, ...patch } : rule))
    onChange(next)
  }

  function addRule() {
    onChange([...value, emptyRule()])
  }

  function removeRule(index) {
    onChange(value.filter((rule, i) => i !== index))
  }

  function handleBrandChange(index, brandId) {
    // เลือกแบรนด์ใหม่แล้วกิจกรรมเดิมอาจไม่ได้อยู่ใต้แบรนด์นั้น ต้องล้างทิ้ง
    updateRule(index, { brandId: brandId || null, activityId: null })
    loadActivities(brandId)
  }

  function searchHero(keyword) {
    setHeroKeyword(keyword)
    dispatch(getAllHeroes({ ...HERO_SEARCH_OPTIONS, keyword: '%' + keyword + '%' }))
  }

  /** ข้อความตัวอย่างต้องตรงกับที่ backend ประกอบให้แอป (RewardServiceImpl.describeRule) */
  function previewText(rule) {
    if (!rule.gainTypeId) {
      return null
    }
    const gainType = (gainTypeList || []).find((type) => type.id === rule.gainTypeId)
    let text = 'พอยท์จาก' + (gainType ? gainType.name : 'ประเภท ' + rule.gainTypeId)

    if (rule.heroId) {
      const hero = heroOptions.find((item) => item.id === rule.heroId)
      text += 'ให้ ' + (hero ? hero.name : 'ฮีโร่ ' + rule.heroId)
    }
    if (rule.activityId) {
      const activity = (activitiesByBrand[rule.brandId] || [])
        .find((item) => item.id === rule.activityId)
      text += ' ในกิจกรรม ' + (activity ? activity.activityName : rule.activityId)
    }
    // ไม่ใส่ช่องทางในข้อความที่ผู้ใช้เห็น — เป็นรายละเอียดภายในที่ไม่ช่วยให้ผู้ใช้ตัดสินใจ
    return text
  }

  const previews = value.map(previewText).filter((text) => !_.isNil(text))

  return (
    <div>
      {value.map((rule, index) => (
        <div key={index}>
          {index > 0 && (
            <div style={{ textAlign: 'center', color: '#6ebf4b', fontWeight: 600, margin: '4px 0' }}>
              — หรือ —
            </div>
          )}
          <div style={{ border: '1px solid #e8e8e8', borderRadius: 4, padding: 12, marginBottom: 8 }}>
            <Row type="flex" justify="end" style={{ marginBottom: 8 }}>
              <Col>
                <Button type="danger" ghost size="small" onClick={() => removeRule(index)}>
                  <Icon type="delete" /> ลบเงื่อนไข
                </Button>
              </Col>
            </Row>

            <div style={{ marginBottom: 4 }}>
              <span style={{ color: '#f5222d' }}>* </span>ประเภทการได้พอยท์
            </div>
            <Select
              style={{ width: '100%', marginBottom: 12 }}
              placeholder="เลือกประเภทการได้พอยท์"
              value={rule.gainTypeId}
              onChange={(gainTypeId) =>
                // เปลี่ยนประเภทแล้วมิติเดิมอาจใช้กับประเภทใหม่ไม่ได้ ต้องล้างทิ้ง
                updateRule(index, {
                  gainTypeId,
                  channel: null,
                  heroId: null,
                  activityId: null,
                  brandId: null
                })
              }>
              {(gainTypeList || []).map((type) => (
                <Select.Option key={type.id} value={type.id}>
                  {type.name}
                </Select.Option>
              ))}
            </Select>

            {!rule.gainTypeId && (
              <div style={{ color: '#999' }}>
                เลือกประเภทการได้พอยท์ก่อน จึงจะระบุเงื่อนไขย่อยได้
              </div>
            )}

            {GAIN_TYPE_WITH_CHANNEL.includes(rule.gainTypeId) && (
              <div>
                <div style={{ marginBottom: 4 }}>ช่องทาง</div>
                <Select
                  style={{ width: '100%', marginBottom: 12 }}
                  placeholder="ทั้งหมด"
                  allowClear
                  value={rule.channel || undefined}
                  onChange={(channel) => updateRule(index, { channel: channel || null })}>
                  {POINT_CHANNEL_SELECTABLE.map((channel) => (
                    <Select.Option key={channel.value} value={channel.value}>
                      {channel.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {GAIN_TYPE_WITH_HERO.includes(rule.gainTypeId) && (
              <div>
                <div style={{ marginBottom: 4 }}>ฮีโร่</div>
                <Select
                  style={{ width: '100%', marginBottom: 12 }}
                  placeholder="ทั้งหมด — พิมพ์เพื่อค้นหา"
                  showSearch
                  allowClear
                  filterOption={false}
                  notFoundContent={heroLoading ? <Spin size="small" /> : null}
                  onSearch={searchHero}
                  value={rule.heroId || undefined}
                  onChange={(heroId) => updateRule(index, { heroId: heroId || null })}>
                  {heroOptions.map((hero) => (
                    <Select.Option key={hero.id} value={hero.id}>
                      {hero.name}
                    </Select.Option>
                  ))}
                  {rule.heroId && !heroOptions.find((hero) => hero.id === rule.heroId) && (
                    <Select.Option key={rule.heroId} value={rule.heroId}>
                      {'ฮีโร่ ' + rule.heroId + ' (ไม่พบในรายการ)'}
                    </Select.Option>
                  )}
                </Select>
              </div>
            )}

            {GAIN_TYPE_WITH_ACTIVITY.includes(rule.gainTypeId) && (
              <div>
                <div style={{ marginBottom: 4 }}>แบรนด์</div>
                <Select
                  style={{ width: '100%', marginBottom: 12 }}
                  placeholder="ทั้งหมด"
                  allowClear
                  value={rule.brandId || undefined}
                  onChange={(brandId) => handleBrandChange(index, brandId)}>
                  {(brandList || []).map((brand) => (
                    <Select.Option key={brand.id} value={brand.id}>
                      {brand.name}
                    </Select.Option>
                  ))}
                </Select>

                <div style={{ marginBottom: 4 }}>กิจกรรม</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder={
                    rule.brandId ? 'ทั้งหมด' : 'เลือกแบรนด์ก่อนจึงจะเลือกกิจกรรมได้'
                  }
                  allowClear
                  disabled={!rule.brandId}
                  value={rule.activityId || undefined}
                  onChange={(activityId) =>
                    updateRule(index, { activityId: activityId || null })
                  }>
                  {(activitiesByBrand[rule.brandId] || []).map((activity) => (
                    <Select.Option key={activity.id} value={activity.id}>
                      {activity.activityName}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        </div>
      ))}

      <Button type="dashed" block onClick={addRule} style={{ marginBottom: 8 }}>
        <Icon type="plus" /> เพิ่มเงื่อนไข
      </Button>

      {previews.length > 0 && (
        <Alert
          type="info"
          message="ผู้ใช้จะเห็นว่า"
          description={
            <div>
              {previews.map((text, index) => (
                <div key={index}>
                  {index > 0 && <span style={{ color: '#6ebf4b' }}>หรือ </span>}
                  {text}
                </div>
              ))}
            </div>
          }
        />
      )}
    </div>
  )
}

ScopeRuleBuilder.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  gainTypeList: PropTypes.array
}

export default ScopeRuleBuilder
